import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthFacade } from 'src/app/auth/auth.facade';
import { PENDING_IDP_KEY } from 'src/app/auth/models/auth-storage-keys';
import { IdpConfigId } from 'src/app/auth/models/idp-config-id.enum';
import { LoggingService } from 'src/app/services/logging.service';
import { AppConfigService } from 'src/app/services/app-config.service';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';

/**
 * Receives the redirect from the Commerce platform in `/sso/relay?accountId=...`.
 *
 * Flow:
 * 1. Persists the `accountId` in `localStorage`.
 * 2. Determines the first IdP to try:
 *    - PingID (oidc) when `oidc.client_id` is configured in the environment.
 *    - Okta directly when CIAM is not yet registered (empty `client_id`), avoiding
 *      an unrecoverable error from PingID before any fallback can run.
 * 3. Marks `sessionStorage['pendingIdp']` and triggers the silent login (`prompt=none`).
 * 4. AppComponent.handleAuthFallback() handles the result:
 *    - oidc success / okta success → navigate to /main.
 *    - oidc failure → fallback to Okta.
 *    - okta failure (or both failed) → navigate to /login-error.
 */
@Component({
  selector: 'app-login-relay',
  standalone: true,
  template: ``,
  styles: [],
})
export class LoginRelayComponent implements OnInit {
  private readonly authFacade = inject(AuthFacade);
  private readonly appConfigService = inject(AppConfigService);
  private readonly route = inject(ActivatedRoute);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly logger = inject(LoggingService);

  ngOnInit(): void {
    this.saveAccountId();
    const firstIdp = this.appConfigService.config.oidc?.client_id
      ? IdpConfigId.Oidc
      : IdpConfigId.Okta;

    sessionStorage.setItem(PENDING_IDP_KEY, firstIdp);
    this.authFacade.loginWith(firstIdp, { prompt: 'none' });
  }

  private saveAccountId(): void {
    const accountId = this.route.snapshot.queryParamMap.get('accountId');

    if (!accountId) {
      this.logger.warn('AccountId not found in query parameters');
      return;
    }

    this.localStorageService.setItem('accountId', accountId);
  }
}
